import axios, { AxiosError } from "axios";
import { useContext, useEffect, useState } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { LighterEndpointEnum } from "../../../enums/endpoints";
import { ILighterPositionsResponse, LighterDeployment } from "../../../interfaces/lighter";
import { LIGHTER_API_URL } from "../../../utils/env";

export function isFreshLighterSnapshot(
  data: ILighterPositionsResponse | undefined,
  now = Date.now()
): boolean {
  const age = now - Date.parse(data?.observedAt ?? "");
  return Number.isFinite(age) && age >= -5000 && age <= 60_000;
}

export const fetchLighterPositions = async (
  tokenId: string,
  deployment: LighterDeployment
): Promise<ILighterPositionsResponse> => {
  const response = await axios.get<{ success: boolean; data: ILighterPositionsResponse }>(
    LIGHTER_API_URL(LighterEndpointEnum.ACCOUNT_POSITIONS),
    {
      withCredentials: true,
      headers: { Authorization: `Bearer ${tokenId}` },
      params: { deployment },
      timeout: 10_000,
    }
  );
  const snapshot = response.data?.data;
  if (!response.data?.success || snapshot?.deployment !== deployment ||
      !Array.isArray(snapshot?.positions) || !isFreshLighterSnapshot(snapshot)) {
    throw new Error(`Lighter ${deployment} positions unavailable or stale`);
  }
  return snapshot;
};

export const useLighterPositions = (deployment: LighterDeployment = "robinhood") => {
  const { tokenId } = useContext(AuthContext);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);
  const result = useSWR<ILighterPositionsResponse, AxiosError>(
    tokenId ? [LIGHTER_API_URL(LighterEndpointEnum.ACCOUNT_POSITIONS), deployment, tokenId] : null,
    () => fetchLighterPositions(tokenId!, deployment),
    { refreshInterval: 10_000 }
  );
  return {
    ...result,
    data: result.data?.positions,
    observedAt: result.data?.observedAt,
    available: !result.error && isFreshLighterSnapshot(result.data, now),
    deployment,
  };
};
